import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"
import {init, dispose, addLine, addVLine, drawObject, saveOverlays, setShapeType, initHisChart, drawHisTradeLine } from "../chart"

const consumer = createConsumer()

// Connects to data-controller="k-chart"
export default class extends Controller {
  static targets = [ "el", "time" ]
  static values = {
    data: Object,
    reviewId: String,
    type: String,
    period: Number,
    draws: Array,
    showTitle: String,
    changeSymbol: String,
    countDown: String,
    handleTrade: String,
    height: String,
    avgCost: Number,
    bars: Number,
    barCount: String,
    showTools: String,
    highPect: Number,

    trades: Array,
    entryPrice: Number,
    entryTime: Number,
    exitPrice: Number,
    exitTime: Number,
    tradeReview: String,
    tradeType: String
  }

  static constants = {
    EVENTS: {
      ACT_CLICKED: "act_clicked",
      CHANGE_TARGET_SYMBOL: "change_target_symbol",
    },
    KEYS: {
      NEXT: ['n', 'ArrowRight'],
      SELL: ['ArrowDown', 'j', 's'],
      BUY: ['ArrowUp', 'k', 'b'],
      DELETE: ['x'],
    },
    COLORS: {
      SL: 'red',
      TP: 'green',
      TPH: 'blue',
      DEFAULT_LINE: '#c0c0c0',
      PC_LINE: 'black',
      SESSION_LINE: 'gray',
      TEXT_WHITE: 'white',
      BUY_ARROW: 'green',
      SELL_ARROW: 'red',
    },
    OVERLAY_GROUPS: {
      DRAW: 'draw',
      TRADE: 'trade',
      HL: 'hl',
    },
    PRECISION_CURRENCIES: ['EURUSD', 'AUDUSD', 'NZDUSD', 'GBPUSD', 'USDCAD', 'USDCHF'],
  }


  connect() {
    if(this.tradeReviewValue === 'true') {
      this.kchart = initHisChart(this.element, this.dataValue.bars)
      this.setStyle();
      this.setChartSize();
      if (this.hasEntryPriceValue && this.hasEntryTimeValue && this.hasExitPriceValue && this.hasExitTimeValue) {
        drawHisTradeLine(this.kchart, 
          { time: this.entryTimeValue, price: this.entryPriceValue },
          { time: this.exitTimeValue, price: this.exitPriceValue },
          this.tradeTypeValue
        )
      }

      if(this.hasTradesValue) {
        this.tradesValue.forEach((trade) => {
          console.log(trade)
          drawHisTradeLine(this.kchart, 
            { time: trade.entry_time, price: trade.entry_price },
            { time: trade.exit_time, price: trade.exit_price },
            trade.trade_type, true
          )
        })
      }
    } else {
      this._initChart()
      this._setupEventListeners()
      this._loadInitialData()
      this._setupActionCable()
    }

    if (this.changeSymbolValue === 'true') {
      this.changeTargetSymbol();
    }

    this.kchart.saveDrawObjects = this.save.bind(this);
  }

  disconnect() {
    document.removeEventListener(this.constructor.constants.EVENTS.ACT_CLICKED, this.boundHandleActClickedEvent);
    document.removeEventListener('keydown', this.handleKeydown);
    window.removeEventListener('resize', this.boundSetChartSize);

    if (this.timer) {
      clearInterval(this.timer)
    }
    if (this.channel) {
      this.channel.unsubscribe()
    }
    dispose(this.elTarget.id)
  }

  // #region Private Setup Methods
  _initChart() {
    const chartId = `${this.reviewIdValue.toLowerCase()}_chart_${Math.floor(Math.random() * 1000)}`;
    this.elTarget.id = chartId;
    this.kchart = init(chartId);

    this.setStyle();

    const barWidth = (window.innerWidth - 70) / this.default_number();
    this.kchart.setBarSpace(barWidth);

    this.setChartSize();
  }

  _setupEventListeners() {
    this.boundHandleActClickedEvent = this.handleActClickedEvent.bind(this);
    document.addEventListener(this.constructor.constants.EVENTS.ACT_CLICKED, this.boundHandleActClickedEvent);

    this.boundSetChartSize = this.setChartSize.bind(this);
    window.addEventListener('resize', this.boundSetChartSize);

    this.handleKeydown = this.handleKeydown.bind(this);
    document.addEventListener('keydown', this.handleKeydown);
  }

  _loadInitialData() {
    this.kchart.applyNewData(this.dataValue.bars);
    this.split = this.dataValue.split;
    this.remain = [];

    if (this.split) {
      this.remain = this.dataValue.remain;
      this.addNewData();
    }

    if (this.handleTradeValue === 'true') {
      this.drawLines();
    }

    if (this.drawsValue && this.drawsValue.length > 0) {
      this.kchart.removeOverlay({ groupId: this.constructor.constants.OVERLAY_GROUPS.DRAW });
      this.drawsValue.forEach((item) => {
        this.kchart.createOverlay(item);
      });
    }
  }

  _setupActionCable() {
    const showTitle = this.showTitleValue === 'true';
    const handleTrade = this.handleTradeValue === 'true';

    this.channel = consumer.subscriptions.create({
      channel: "MdChannel",
      symbol: this.reviewIdValue,
      period: this.periodValue,
    }, {
      received: (data) => {
        if (data.type === 'bars') {
          const bars = Array.isArray(data.bars) ? data.bars : [data.bars];
          bars.forEach(bar => this.kchart.updateData(bar));

          if (showTitle && bars.length > 0) {
            document.title = bars[0].close;
          }
        } else if (data.type === 'lines' && handleTrade) {
          Object.keys(data.lines).forEach((id) => {
            const price = data.lines[id];
            if (price) {
              if(id == "SL" || id == "TP") {
                this.drawTradesLine(id, price);
              } else {
                addLine(this.kchart, id, price, price, this.constructor.constants.COLORS.DEFAULT_LINE, true, this.constructor.constants.OVERLAY_GROUPS.HL, {size: 2})
              }
            }
          });
        } else {
          console.log("Received unhandled data from MdChannel:", data);
        }
      }
    });
  }
  // #endregion

  // #region API Methods
  async _apiFetch(path, options = {}) {
    const { method = 'GET', body = null } = options;

    const headers = {
      'Content-Type': 'application/json',
      'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
    };

    const config = {
      method,
      headers,
    };

    if (body) {
      config.body = JSON.stringify(body);
    }

    try {
      const response = await fetch(path, config);
      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.indexOf("application/json") !== -1) {
        return await response.json();
      }
      return;
    } catch (error) {
      console.error('API Fetch Error:', error);
      throw error;
    }
  }

  loadLog(event) {
    event.preventDefault();
		if(this.remain.length > 0) {
			for(let i =0 ;i < this.remain.length; i++ ) {
				this.kchart.applyNewData(this.remain[i])
			}

      if(this.toolsValue == 'true')
        this.kchart.setOffsetRightDistance(this.calBarWidth() * 2)
      else
        this.kchart.setOffsetRightDistance(0)
			this.remain = []
		}

    const path = `/reviews/${this.reviewIdValue}/last_review_log`;
    this._apiFetch(path)
      .then(log => {
        console.log("Loaded log:", log);
        if (log && log.data) {
          this.kchart.removeOverlay({ groupId: this.constructor.constants.OVERLAY_GROUPS.DRAW });
          log.data.forEach(item => this.kchart.createOverlay(item));
        }
      })
      .catch(error => console.error('Error loading log:', error));
  }

  save(event) {
    if (event) event.preventDefault();

    const data = saveOverlays(this.kchart, { groupId: this.constructor.constants.OVERLAY_GROUPS.DRAW });
    console.log("Saving data:", data);

    let path = `/reviews/${this.reviewIdValue}/review_logs`;
    console.log(this.typeValue)
    if (this.typeValue === 'fx') {
      path = `/chart/${this.reviewIdValue}/save`;
    }
    console.log(path)

    const payload = { review_log: { data: data } };

    this._apiFetch(path, { method: 'POST', body: payload })
      .then(response => console.log("Save successful:", response))
      .catch(error => console.error('Error saving log:', error));
  }
  // #endregion

  right_distance() {
    if(this.toolsValue == 'true') {
      return this.calBarWidth() * 2;
    } else {
      return 0;
    }
  }
  default_number() {
    if(this.typeValue == 'fx') {
      return this.barsValue;
    } else {
      return 83
    }
  }

  changeTargetSymbol() {
    const event = new CustomEvent(this.constructor.constants.EVENTS.CHANGE_TARGET_SYMBOL, {
      bubbles: true,
      detail: { symbol: this.reviewIdValue }
    })
    this.element.dispatchEvent(event)
  }

  drawLines() {
    const {COLORS, OVERLAY_GROUPS} = this.constructor.constants;
    Object.keys(this.dataValue.hl).forEach((key) => {
      let value = this.dataValue.hl[key];
      if(!value) return;
      if(key == "SL" || key == "TP") {
        this.drawTradesLine(key, value);
      } else {
        addLine(this.kchart, key, value, value, COLORS.DEFAULT_LINE, true, OVERLAY_GROUPS.HL, {size: 2})
      }
    })

    addVLine(this.kchart, 'pc', this.dataValue.pc, 'pc', COLORS.PC_LINE, true, OVERLAY_GROUPS.HL, {size: 2})
    if(this.dataValue.euro_open)
    addVLine(this.kchart, 'euro_open', this.dataValue.euro_open, 'euro_open', COLORS.SESSION_LINE, true, OVERLAY_GROUPS.HL, {size: 2})
    if(this.dataValue.us_open)
    addVLine(this.kchart, 'us_open', this.dataValue.us_open, 'us_open', COLORS.SESSION_LINE, true, OVERLAY_GROUPS.HL, {size: 2})
  }

  handleActClickedEvent(event) {
    if (this.reviewIdValue !== event.detail.targetId) return;

    const { symbol, name } = event.detail;
    console.log("Act event clicked (heard by k-chart):", event.detail);

    const data = this.kchart.getDataList();
    const lastBar = data.length > 0 ? data[data.length - 1] : null;
    if (!lastBar) return;

    const isEurUsd = symbol === 'EURUSD';

    const actionMap = {
      'ACT_DLONG': {
        text: isEurUsd ? '---v' : '^---',
        baseline: isEurUsd ? 'bottom' : 'top',
      },
      'ACT_CLOSE_HALF': { text: '-----', baseline: 'bottom' },
      'ACT_DCLOSE': { text: '=====', baseline: 'bottom' },
    };

    const defaultAction = {
      text: isEurUsd ? '^---' : '---v',
      baseline: isEurUsd ? 'top' : 'bottom',
    };

    const action = actionMap[name] || defaultAction;

    setShapeType(this.kchart, 'text', {}, {
      color: this.constructor.constants.COLORS.TEXT_WHITE,
      text: action.text,
      baseline: 'middle',
      size: 20,
    }, [{
      timestamp: lastBar.timestamp,
      value: lastBar.close,
    }]);

    this.save(event);
  }

	calBarWidth() {
		return (window.innerWidth-70)/this.default_number();
	}

	addNewData() {
		if(this.remain.length > 0) {
			this.last_bar = this.remain.shift()
			this.kchart.updateData(this.last_bar)
      if(this.toolsValue == 'true')
        this.kchart.setOffsetRightDistance(this.calBarWidth() * (this.remain.length + 1))
      else
        this.kchart.setOffsetRightDistance(0)
		}
	}

  activeDrawTool(e) {
    const tool = e.target.dataset.tool
    drawObject(tool, this.kchart)
  }

  getPointValue(overlay) {
    if(!overlay) return undefined;
    const points = overlay.points
    if (Array.isArray(points) && points.length > 0) {
      const price = points[0].value
      return price;
    }

    return undefined
  }

  drawTrades(e) {
    this.drawTradesLine(e.currentTarget.dataset.type)
  }

  drawTradesLine(type, price = undefined) {
    const {COLORS, OVERLAY_GROUPS} = this.constructor.constants;
    const ext_attrs = {style: 'solid', size: 2}

    const colorMap = {
      'SL': COLORS.SL,
      'TP': COLORS.TP,
      'TPH': COLORS.TPH,
    };
    const color = colorMap[type] || COLORS.TP;

    let res = this.kchart.getOverlays({ id: type, groupId: OVERLAY_GROUPS.TRADE });
    let method = "createOverlay";
    if (res.length > 0) {
      method = "overrideOverlay";
    }

    let points = []
    if(price) points = [{value: price}]
    this.kchart[method]({
      name: "simpleTag",
      id: type,
      extendData: type,
      points: points,
      lock: false,
      onDrawEnd: (event) => {
        const { overlay } = event;
        const price = this.getPointValue(overlay);
        if(method == "createOverlay")
          this.channel.perform("set_line", { id: overlay.id, price: price, symbol: this.reviewIdValue })
      },
      onPressedMoveEnd: (event) => {
        const { overlay } = event;
        const price = this.getPointValue(overlay);
        this.channel.perform("set_line", { id: overlay.id, price: price, symbol: this.reviewIdValue })
      },
      onRemoved: (event) => {
        const { overlay } = event;
        this.channel.perform("remove_line", { id: overlay.id, symbol: this.reviewIdValue })
      },
      groupId: OVERLAY_GROUPS.TRADE,
      styles: {
        line: {
          size: ext_attrs.size || 2,
          color: color,
          style: ext_attrs.style || "solid",
        },
        text: {
          color: COLORS.TEXT_WHITE,
          backgroundColor: color,
        },
      },
    });
  }

  handleKeydown(e) {
    const {KEYS, COLORS} = this.constructor.constants;

    if(this.split && this.remain.length > 0) {
      if(KEYS.NEXT.includes(e.key)) {
				this.addNewData();
      }

      const createTextOverlay = (text, color, baseline, value) => {
        return setShapeType(this.kchart, 'text', {}, {
          color, text, baseline, size: 20,
        }, [{
          timestamp: this.last_bar.timestamp,
          value
        }]);
      };

			if(KEYS.SELL.includes(e.key)) {
				if(e.key != 's') this.addNewData();
				this.last_create_overlay_id = createTextOverlay('🢃', COLORS.SELL_ARROW, 'bottom', this.last_bar.high + 0.5);
				return
			}

			if(KEYS.BUY.includes(e.key)) {
				if(e.key != 'b') this.addNewData();
				this.last_create_overlay_id = createTextOverlay('️🢁', COLORS.BUY_ARROW, 'top', this.last_bar.low - 0.5);
				return
			}

			if(KEYS.DELETE.includes(e.key)) {
				this.kchart.removeOverlay({id: this.last_create_overlay_id})
				return
			}
    }

    drawObject(e.key, this.kchart)
  }

  // #region Style Methods
  setStyle() {
    this._setChartPrecision();
    this._createChartIndicators();
    this._applyChartTheme();
  }

  _setChartPrecision() {
    const precision = this.constructor.constants.PRECISION_CURRENCIES.includes(this.reviewIdValue)
      ? { price: 5, volume: 2 }
      : { price: 2, volume: 2 };
    this.kchart.setPrecision(precision);
  }

  _createChartIndicators() {
    const paneId = "candle_pane";
    this.kchart.createIndicator({name: "EMA", calcParams: [20]},true,{id: paneId});
    if(this.barCountValue === 'true') {
      this.kchart.createIndicator("barCount", true, { id: paneId });
      this.kchart.createIndicator({name: "barType", styles: {forChart: this.typeValue}}, true, { id: paneId });
    }
    if(this.countDownValue === 'true') {
      this.kchart.createIndicator("barTimeCountdown", true, { id: paneId });
    }
    if(this.typeValue == 'fx') {
      // this.kchart.createIndicator("3BarMicroChannel", true, { id: "candle_pane" });
      // this.kchart.createIndicator({name: "ATL", calcParams: 'SATL'},true,{id: "candle_pane"});
      // this.kchart.createIndicator({name: "ATL", calcParams: 'FATL'},true,{id: "candle_pane"});
      // this.kchart.createIndicator("IBS", true, { id: "candle_pane" });
      this.kchart.createIndicator("AMA", true, { id: "candle_pane" });
      this.kchart.createIndicator("Midpoint", true, { id: "candle_pane" });
    }
  }

  _applyChartTheme() {
    let showPriceMark = this.showToolsValue === 'true';
    let barStyle = {};
    // 'this.night_mode' is not defined in this controller. Assuming it is set elsewhere.
    const theme = this.night_mode ? "dark" : "light";

    if (this.typeValue !== 'fx') {
      showPriceMark = false;
      barStyle = {
        upColor: 'white',
        downColor: '#2c2f2f',
        upBorderColor: '#232525',
        downBorderColor: '#232525',
        upWickColor: '#232525',
        downWickColor: '#232525',
        //noChangeWickColor: '#888888'
      }
      if (theme === "light") {
        this.elTarget.style.backgroundColor = "#ffffff";
      } else if (theme === "dark") {
        this.elTarget.style.backgroundColor = "#1b1b1f";
      }
    } else {
      barStyle = {
        // 'current_open' | 'previous_close'
        compareRule: 'previous_close',
        upColor: '#2DC08E',
        downColor: '#F92855',
        noChangeColor: '#888888',
        upBorderColor: '#2DC08E',
        downBorderColor: '#F92855',
        noChangeBorderColor: '#888888',
        upWickColor: '#2DC08E',
        downWickColor: '#F92855',
        noChangeWickColor: '#888888'

        // // upColor: '#2DC08E',
        // // downColor: '#F92855',
        // noChangeColor: '#404040',
        // upBorderColor: '#2a5a3a',
        // downBorderColor: '#5a2a3a',
        // noChangeBorderColor: '#404040',
        // upWickColor: '#2a5a3a',
        // downWickColor: '#5a2a3a',
        // noChangeWickColor: '#404040'
      }
      // barStype =  {
      //   upColor: 'white',
      //   downColor: '#2c2f2f',
      //   // noChangeColor: '#888888',
      //   upBorderColor: '#232525',
      //   downBorderColor: '#232525',
      //   //noChangeBorderColor: '#888888',
      //   upWickColor: '#232525',
      //   downWickColor: '#232525',
      //   //noChangeWickColor: '#888888'
      // }
    }

    // this.kchart.setOffsetRightDistance(this.right_distance());
    this.kchart.setStyles({
      grid: { show: false },
      // yAxis: { show: showPriceMark, size: showPriceMark ? 'auto' : 0 },
      // xAxis: { show: showPriceMark, size: showPriceMark ? 'auto' : 0 },
      candle: {
        // priceMark: {
        //   show: showPriceMark,
        //   last: { show: showPriceMark },
        // },
        type: this.typeValue === 'fx' ? "candle_stroke" : 'candle_up_stroke',
        bar: barStyle,
        tooltip: {
          show: false,
          showType: "rect",
          showRule: "none",
        },
      },
      indicator: {
        tooltip: {
          showType: "rect",
          showRule: "none",
        },
      },
    });
  }
  // #endregion

  setChartSize() {
    if(this.heightValue) {
      this.elTarget.style.height = this.heightValue;
    } else {
      let innerHeight = window.innerHeight;
      if(this.highPectValue) {
        innerHeight = innerHeight * parseFloat(this.highPectValue) / 100;
      }
      if(this.showToolsValue == 'true') {
        innerHeight -= 55;
      }
      this.elTarget.style.height = innerHeight + "px";
    }

    if(this.kchart) {
      this.kchart.resize();
      let bw = (window.innerWidth-70)/(this.default_number()-1)
      this.kchart.setBarSpace(bw)
    }
  };

  compare(event) {
    event.preventDefault();
    const url = event.target.dataset.url;
    const windowFeatures = "width=1024,height=768,toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes";
    window.open(url, "_blank", windowFeatures);
    this.save(event)
  }

  toggleHotkeysPanel(event) {
    const panel = event.currentTarget.nextElementSibling;
    panel.classList.toggle('show');

    const closePanel = (e) => {
      if (!panel.contains(e.target) && e.target !== event.currentTarget) {
        panel.classList.remove('show');
        document.removeEventListener('click', closePanel);
      }
    };

    setTimeout(() => {
      document.addEventListener('click', closePanel);
    }, 10);
  }

  syncSymbolToChartC(event) {
    if (this.element.closest('[data-turbo-frame="chart_b"]') || this.element.closest('#chart_b')) {
      const symbol = event.currentTarget.dataset.symbol;
      if (symbol) {
        const chartCFrame = document.querySelector('turbo-frame[id="chart_c"]');
        if (chartCFrame) {
          const newSrc = `/chart/15/${symbol}?change_symbol=false&height=580px`;
          chartCFrame.setAttribute('src', newSrc);
        }
      }
    }
  }
}
